/**
 * DGI Tabla E threshold for anonymous e-Ticket (Consumidor Final). When the
 * total is at or below this amount, the receptor block may be a bare
 * `RznSocRecep`; above it, DGI requires TipoDocRecep + DocRecep and rejects
 * the submission with E05 "No cumple validaciones de Formato comprobantes"
 * otherwise.
 *
 * Applies to e-Tick, e-Tick.Cta aj, and their respective NC / ND
 * (cfeType 101, 102, 103, 131, 132, 133).
 *
 * DGI expresses the limit in **Unidad Indexada (UI)** — 5,000 UI — and the
 * UI value in pesos is set by INE / BCU and updates daily. We compare the
 * invoice's total UYU against `5_000 * UI(date)` where the UI for the
 * invoice's date is looked up in the table below.
 *
 * Keep `UI_MONTHLY_VALUES` updated from
 * https://www.gub.uy/direccion-general-impositiva/datos-y-estadisticas/datos/unidad-indexada
 * (use the value for the first business day of each month; intra-month
 * drift is < 0.5% — not material for the threshold gate).
 */
export const ETICKET_CONSUMIDOR_FINAL_UI_THRESHOLD = 5000;
/**
 * UI value (UYU per 1 UI) by YYYY-MM. Used to convert the 5,000 UI threshold
 * to UYU for the invoice being created/edited. Update monthly.
 */
const UI_MONTHLY_VALUES = {
    '2026-01': 6.4237,
    '2026-02': 6.4199,
    '2026-03': 6.4697,
    '2026-04': 6.4979,
    '2026-05': 6.524,
    '2026-06': 6.5583,
    '2026-07': 6.6026,
};
/** Latest known UI value — fallback for dates after the table (e.g. invoice
 * issued today before the next monthly update lands). Keep this in sync with
 * the most recent entry above.
 *
 * WARNING: when the table lags the calendar, `uiValueForDate` returns this
 * (too-low) value for later months, so the 5.000 UI threshold in pesos is
 * understated and the reporte diario over-counts e-Tickets at the boundary
 * → DGI ER02. The UI grows ~0.6%/month, so a stale month can drift the
 * threshold by hundreds of pesos. Keep this table current every month. */
const LATEST_KNOWN_UI = 6.6026;
/** Earliest known UI value — fallback for very old dates outside the table. */
const EARLIEST_KNOWN_UI = 6.4237;
/**
 * UI value in pesos (UYU per 1 UI) for a given ISO date (`YYYY-MM-DD` or
 * `YYYY-MM`). Falls back to the latest known value for future dates and the
 * earliest for very-old dates.
 */
export function uiValueForDate(date) {
    if (!date)
        return LATEST_KNOWN_UI;
    const key = date.slice(0, 7);
    if (key in UI_MONTHLY_VALUES)
        return UI_MONTHLY_VALUES[key];
    /** Pick latest-known vs earliest-known by string compare on YYYY-MM keys. */
    const sortedKeys = Object.keys(UI_MONTHLY_VALUES).sort();
    if (sortedKeys.length === 0)
        return LATEST_KNOWN_UI;
    if (key < sortedKeys[0])
        return EARLIEST_KNOWN_UI;
    return LATEST_KNOWN_UI;
}
/**
 * DGI Tabla E threshold expressed in UYU for the given invoice date.
 * `5000 UI × UI(date)`. Use this for live form gating and backend validation.
 */
export function getEticketThresholdUyu(date) {
    return ETICKET_CONSUMIDOR_FINAL_UI_THRESHOLD * uiValueForDate(date);
}
/**
 * Annual income ceiling for the pequeña empresa / IVA mínimo regime —
 * literal E) of the Título 4 income-tax article (art. 52 in T.O. 1996,
 * which is how Decreto 206/019 cites it; renumbered to art. 66 in
 * T.O. 2023, which is how DGI cites it today).
 *
 * Expressed in UI by the norm, exactly like the Tabla E limit above, and
 * for the same reason: the peso figure moves every year and the UI one
 * does not.
 */
export const LITERAL_E_ANNUAL_UI_THRESHOLD = 305000;
/**
 * The literal E ceiling in pesos for a fiscal year.
 *
 * Converted at the UI value in force at the START of the exercise (1
 * January), NOT at today's UI — that is how DGI computes and publishes
 * it, and using the current UI instead would quote a ceiling that drifts
 * upward through the year while the real one stays fixed.
 *
 * Verified against DGI's own published figure: 305.000 UI × 6.4237
 * (January 2026) = $1.959.229, which is the number on
 * https://www.gub.uy/direccion-general-impositiva/comunicacion/publicaciones/tope-ingresos-anuales-para-pequenas-empresas-iva-minimo
 * for 2026. If this stops matching, the UI table above has drifted.
 */
export function getLiteralEAnnualThresholdUyu(year) {
    return LITERAL_E_ANNUAL_UI_THRESHOLD * uiValueForDate(`${year}-01`);
}
/** CFE types subject to the Tabla E receptor-identification rule. */
export const ETICKET_TYPES_WITH_TABLA_E = ['101', '102', '103', '131', '132', '133'];
export function isEticketWithTablaE(cfeType) {
    return ETICKET_TYPES_WITH_TABLA_E.includes(cfeType);
}
/**
 * @deprecated Use `getEticketThresholdUyu(invoiceDate)` instead — the limit
 * is 5,000 UI, not a fixed pesos amount. Kept only for callers that haven't
 * been migrated yet. Value here uses the most-recent UI from the table.
 */
export const ETICKET_CONSUMIDOR_FINAL_UYU_THRESHOLD = ETICKET_CONSUMIDOR_FINAL_UI_THRESHOLD * LATEST_KNOWN_UI;
